import React, { ComponentType } from 'react';
import { t as uiText } from '../i18n';
import { shouldSuppressUpdateBanner } from './embedContext';
import {
  UPDATE_REQUIRED_EVENT,
  beginChunkReload,
  isChunkReloadSpent,
  isDeployChunkError,
} from './chunkReload';

const quietFont =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Hiragino Sans", "Noto Sans JP", sans-serif';

/** 公開面で自動復旧を使い切ったあと。更新とは言わない。 */
function PublicChunkFailed() {
  return React.createElement(
    'div',
    {
      style: {
        minHeight: 160,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 24,
        fontFamily: quietFont,
        color: '#3d3429',
        textAlign: 'center',
      },
    },
    React.createElement(
      'p',
      { style: { margin: 0, fontSize: '0.95rem', lineHeight: 1.6 } },
      uiText('This page could not be loaded.')
    ),
    React.createElement(
      'button',
      {
        type: 'button',
        onClick: () => window.location.reload(),
        style: {
          padding: '10px 22px',
          backgroundColor: '#cfae91',
          color: '#fff',
          border: 'none',
          borderRadius: 9999,
          cursor: 'pointer',
          fontWeight: 500,
          fontSize: '0.9375rem',
          fontFamily: quietFont,
        },
      },
      uiText('Reload')
    )
  );
}

/** 非公開面はバナーに任せ、ルートの ErrorBoundary には投げない。 */
function KeepCurrentScreen() {
  return null;
}

/**
 * 消えた chunk だけを扱う。
 * 公開面は同一タブで1回だけ再読み込みし、非公開面は案内を出して画面を残す。
 * API エラーや一時的なネットワークエラーでは再読み込みしない。
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    importFn().catch((error: unknown) => {
      if (!isDeployChunkError(error)) {
        throw error;
      }

      if (shouldSuppressUpdateBanner()) {
        if (!isChunkReloadSpent() && beginChunkReload()) {
          window.location.reload();
          return new Promise<{ default: T }>(() => {});
        }
        return { default: PublicChunkFailed as unknown as T };
      }

      window.dispatchEvent(new CustomEvent(UPDATE_REQUIRED_EVENT));
      return { default: KeepCurrentScreen as unknown as T };
    })
  );
}

/**
 * Named export variant — use when the module doesn't have a default export.
 * Usage: lazyWithRetryNamed(() => import('./Foo'), 'Foo')
 */
export function lazyWithRetryNamed<
  M extends Record<string, any>,
  K extends keyof M
>(
  importFn: () => Promise<M>,
  exportName: K
): React.LazyExoticComponent<M[K]> {
  return lazyWithRetry(() =>
    importFn().then((m) => ({ default: m[exportName] }))
  );
}
